import axios from "axios";
import { Case, Party, Document, Event, Deadline, Evidence } from "../types";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "https://legal.5bc.us/api";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// ==================== CASE API ====================

export const caseAPI = {
  getAll: async (status?: string) => {
    const params = status ? { status } : {};
    const response = await api.get<Case[]>("/cases/", { params });
    return response.data;
  },

  getById: async (caseId: number) => {
    const response = await api.get<Case>(`/cases/${caseId}`);
    return response.data;
  },

  create: async (caseData: Partial<Case>) => {
    const response = await api.post<Case>("/cases/", caseData);
    return response.data;
  },

  update: async (caseId: number, caseData: Partial<Case>) => {
    const response = await api.put<Case>(`/cases/${caseId}`, caseData);
    return response.data;
  },
};

export const db1995API = {
  list: async (
    caseCode?: string,
    limit: number = 200000,
    caseId?: number
  ) => {
    const response = await api.get("/db1995", {
      params: {
        ...(caseCode ? { case_code: caseCode } : {}),
        limit,
        ...(caseId ? { case_id: caseId } : {}),
      },
    });
    return response.data;
  },
  update: async (
    rowId: number,
    payload: { index_name?: string; reference_title?: string; verified?: boolean }
  ) => {
    const response = await api.patch(`/db1995/${rowId}`, payload);
    return response.data;
  },
  getCatalog: async () => {
    const response = await api.get("/db1995/catalog");
    return response.data;
  },
  getCatalogSection: async (section: string, offset: number = 0, limit: number = 100) => {
    const response = await api.get("/db1995/catalog-section", {
      params: { section, offset, limit },
    });
    return response.data;
  },
  search: async (q: string, limit: number = 1000) => {
    const response = await api.get("/db1995/search", {
      params: { q, limit },
    });
    return response.data;
  },
};

// ==================== DOCUMENT API ====================

export const documentAPI = {
  upload: async (file: File, caseId: number, documentType: string) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("case_id", caseId.toString());
    formData.append("document_type", documentType);

    const response = await api.post("/documents/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  },

  getByCaseId: async (caseId: number) => {
    const response = await api.get<Document[]>(`/documents/${caseId}`);
    return response.data;
  },

  update: async (documentId: number, data: { event_id?: number; document_type?: string }) => {
    const response = await api.patch<Document>(`/documents/${documentId}`, data);
    return response.data;
  },

  delete: async (documentId: number) => {
    const response = await api.delete(`/documents/${documentId}`);
    return response.data;
  },

  /** Replace a stored TIFF with a PDF (same document row). */
  convertTiffToPdf: async (documentId: number) => {
    const response = await api.post(
      `/documents/${documentId}/convert-tiff-to-pdf`
    );
    return response.data;
  },

  analyze: async (documentId: number) => {
    const response = await api.post(`/documents/${documentId}/analyze`);
    return response.data;
  },

  getSavedAnalysis: async (documentId: number) => {
    const response = await api.get(`/documents/${documentId}/analysis`);
    return response.data;
  },

  analyzeCombined: async (documentIds: number[]) => {
    const response = await api.post(
      "/documents/analyze-combined",
      { document_ids: documentIds },
      {
        // Combined analysis can take many minutes (many PDFs + large model response).
        timeout: 900000,
      }
    );
    return response.data;
  },

  copyToDb1995: async (documentId: number) => {
    const response = await api.post(`/documents/${documentId}/copy-to-db1995`);
    return response.data;
  },

  copyCasePdfsToDb1995: async (caseId: number) => {
    const response = await api.post(`/cases/${caseId}/copy-pdfs-to-db1995`);
    return response.data;
  },
};

// ==================== AI ANALYSIS API ====================

export const aiAPI = {
  getPrecedents: async (caseId: number) => {
    const response = await api.get(`/cases/${caseId}/precedents`);
    return response.data;
  },

  getRiskAnalysis: async (caseId: number) => {
    const response = await api.get(`/cases/${caseId}/risk-analysis`);
    return response.data;
  },

  getTimeline: async (caseId: number) => {
    const response = await api.get(`/cases/${caseId}/timeline`);
    return response.data;
  },

  getOmissions: async (caseId: number) => {
    const response = await api.get(`/cases/${caseId}/omissions`);
    return response.data;
  },

  getPatterns: async (caseId: number) => {
    const response = await api.get(`/cases/${caseId}/patterns`);
    return response.data;
  },

  extractAuthoritiesToMac: async (caseId: number) => {
    const response = await api.post(`/cases/${caseId}/extract-authorities-to-mac`);
    return response.data;
  },

  getCombinedAnalysis: async (caseId: number) => {
    const response = await api.get(`/cases/${caseId}/combined-analysis`);
    return response.data;
  },

  getOcrExportUrl: (caseId: number) =>
    `${API_BASE_URL}/cases/${caseId}/ocr-export`,
};

// ==================== EVENT API ====================

export const eventAPI = {
  create: async (eventData: Partial<Event>) => {
    const response = await api.post<Event>("/events/", eventData);
    return response.data;
  },

  getByCaseId: async (caseId: number) => {
    const response = await api.get<Event[]>(`/events/${caseId}`);
    return response.data;
  },
};

// ==================== DEADLINE API ====================

export const deadlineAPI = {
  create: async (deadlineData: Partial<Deadline>) => {
    const response = await api.post<Deadline>("/deadlines/", deadlineData);
    return response.data;
  },

  getUpcoming: async (days: number = 7) => {
    const response = await api.get(`/deadlines/upcoming?days=${days}`);
    return response.data;
  },
};

// ==================== PARTY API ====================

export const partyAPI = {
  create: async (partyData: Partial<Party>) => {
    const response = await api.post<Party>("/parties/", partyData);
    return response.data;
  },

  getByCaseId: async (caseId: number) => {
    const response = await api.get<Party[]>(`/parties/${caseId}`);
    return response.data;
  },
};

// ==================== EVIDENCE API ====================

export const evidenceAPI = {
  create: async (evidenceData: Partial<Evidence>) => {
    const response = await api.post<Evidence>("/evidence/", evidenceData);
    return response.data;
  },

  getByCaseId: async (caseId: number) => {
    const response = await api.get<Evidence[]>(`/evidence/${caseId}`);
    return response.data;
  },

  getDownloadUrl: (evidenceId: number) =>
    `${process.env.REACT_APP_API_URL || "https://legal.5bc.us/api"}/evidence/by-id/${evidenceId}/download`,

  uploadFile: async (evidenceId: number, file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await api.post(
      `/evidence/by-id/${evidenceId}/upload`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    return response.data;
  },

  analyze: async (evidenceId: number) => {
    const response = await api.post(`/evidence/by-id/${evidenceId}/analyze`);
    return response.data;
  },
};

export default api;
